import type { SelfscanActivation, SelfscanOrder } from '@/types';

const requestHeaders = {
  'Content-Type': 'application/json',
  'X-Requested-With': 'XMLHttpRequest',
};

export async function fetchSelfscanOrder(
  orderId: string,
  mailId: string
): Promise<SelfscanOrder> {
  const params = new URLSearchParams({ id: orderId, mailid: mailId });
  const response = await fetch(
    `/of2/private_services/selfscan_get_order?${params.toString()}`,
    {
      method: 'POST',
      headers: requestHeaders,
    }
  );

  if (!response.ok) {
    throw new Error(`Selfscan order request failed: ${response.status}`);
  }

  const data: unknown = await response.json();
  if (!isSelfscanOrder(data)) {
    throw new Error('Invalid selfscan order response');
  }

  return data;
}

export async function activateSelfscanTicket(
  orderId: string,
  mailId: string,
  ticketNumber: string
): Promise<SelfscanActivation> {
  const params = new URLSearchParams({
    order_id: orderId,
    mail_id: mailId,
    ticket_number: ticketNumber,
  });
  const response = await fetch(
    `/of2/public_services/selfscan_active_ticket?${params.toString()}`,
    {
      method: 'POST',
      headers: requestHeaders,
    }
  );

  if (!response.ok) {
    throw new Error(`Selfscan activation request failed: ${response.status}`);
  }

  const data: unknown = await response.json();
  if (!isSelfscanActivation(data)) {
    throw new Error('Invalid selfscan activation response');
  }

  return data;
}

function isSelfscanOrder(data: unknown): data is SelfscanOrder {
  if (!isRecord(data)) return false;

  return (
    data.status === 'confirmed' &&
    (typeof data.orderid === 'number' || typeof data.orderid === 'string') &&
    typeof data.mailid === 'string' &&
    isRecord(data.info) &&
    Array.isArray(data.events)
  );
}

function isSelfscanActivation(data: unknown): data is SelfscanActivation {
  if (!isRecord(data)) return false;

  return (
    data.status === 'success' &&
    (typeof data.order_id === 'number' || typeof data.order_id === 'string') &&
    typeof data.mail_id === 'string' &&
    typeof data.ticket_number === 'string' &&
    typeof data.arrived === 'number'
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}